<template>
  <div v-if="referenceList.length">
    <v-autocomplete
      v-model="selectedReference"
      :items="referenceList"
      label="Reference List"
      item-text="label"
      item-value="key"
      color="blue"
      item-color="info"
      menu-props="light"
    />

    <v-data-table
      class="table-color"
      height="450px"
      :headers="headers"
      :items="referenceOptions"
      fixed-header
      :search="search"
      :items-per-page="-1"
      hide-default-footer
    >
      <template #top>
        <v-container>
          <v-row no-gutters align="center">
            <v-col cols="12" sm="4">
              <v-btn light @click="dialog = true"> New Value <v-icon>add</v-icon> </v-btn>
            </v-col>
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="search"
                label="Quick Search reference value"
                color="blue"
                prepend-inner-icon="search"
                item-color="info"
                menu-props="light"
                class=""
              />
            </v-col>
          </v-row>
        </v-container>
      </template>
      <template #item.actions="{ item, index }">
        <v-btn dark icon @click="editItem(item)">
          <v-icon>edit</v-icon>
        </v-btn>
        <v-btn v-if="!item.disabled" dark icon @click="deleteReference(item)">
          <v-icon>delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <div class="text-center">
      <v-dialog
        v-if="dialog"
        v-model="dialog"
        light
        persistent
        width="700"
      >
        <v-card>
          <v-card-title class="text-h5 grey lighten-2">
            {{ editMode ? 'Edit' : 'Create new' }} value
          </v-card-title>

          <v-form ref="referenceForm" v-model="validForm">
            <v-card-text class="pt-2">
              <v-text-field
                v-model="valueKey"
                label="Key *"
                required
                :disabled="editMode"
                color="info"
                :rules="editMode ? rules.editKey : rules.createKey"
              />

              <v-text-field
                v-model="valueLabel"
                label="Label *"
                color="info"

                required
                :rules="rules.label"
              />

              <v-textarea
                v-model="valueDescription"
                label="Infobulle"
                color="info"
                :hint="`To add a clickable link, use this syntax: '{{{This is my label (link)}}}'`"
                persistent-hint
              />
            </v-card-text>
            <v-card-title v-if="isLabalaguere">
              Icon
              <Cloudinary
                v-model="iconUrl"
                :field="{
                  multivalued: false
                }"
              />
            </v-card-title>
          </v-form>

          <v-divider />

          <v-card-actions>
            <v-spacer />
            <v-btn :disabled="!validForm" color="success" small @click="updateItems()">{{ editMode ? 'Update' : 'Create' }}</v-btn>
            <v-btn color="info" small @click="closeDialog()">Cancel</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, reactive } from 'vue';
import { useStore } from 'vue2-helpers/vuex';

import Cloudinary from '../Cloudinary/Cloudinary.vue';

const props = defineProps({
  workspace: {
    type: String,
    default: null
  },
  referenceConfiguration: {
    type: [Array, Boolean, Number, String],
    default: null
  }
});

// VALUES
const valueLabel = ref(null);
const valueKey = ref(null);
const valueDescription = ref(null);
const systemReference = ref(false);
const iconUrl = ref(null);
const componentRerender = ref(0);

const referenceForm = ref(null);
const dialog = ref(false);
const editMode = ref(false);
const search = ref(null);
const validForm = ref(true);

const headers = [
  { text: 'Key', value: 'key', class: 'header-color' },
  { text: 'Label', value: 'label', class: 'header-color' },
  { text: 'Infobulle', value: 'description', class: 'header-color' },
  { text: 'Actions', value: 'actions', class: 'header-color', sortable: false }
];

const store = useStore();
const activeReference = ref(-5);

const isLabalaguere = computed(() => store.state.environment.PROJECT === 'labalaguere');

const referenceList = computed(() => {
  const reference = store.getters.getReference(`allowed_reference_to_update_${props.workspace}`) || [];
  return Object.entries(reference).map(([key, value]) => ({
    key: key,
    label: value
  })) || [];
});

const getFirstSelected = computed(() => (referenceList.value && referenceList.value[0] ? referenceList.value[0].key : null));

const selectedReference = ref(getFirstSelected.value);

const key = [v => !!v || 'required', v => new RegExp('^$|([^\\s]*)^([a-zA-Z0-9_]*)+$').test(v) || 'Invalid Input! ex:serve_extension_new'];

// INPUT RULES
const rules = reactive({
  editKey: key,

  createKey: [
    ...key,
    v => !Object.keys(store.getters.getReference(selectedReference.value)).includes(v) || 'Key already exists'
  ],
  label: [
    v => !!v || 'required'
  ]
});

const referenceOptions = computed(() => {
  const buildetValue = [];
  if (!selectedReference.value) {
    return [];
  }
  const reference = store.getters.getReference(selectedReference.value) || [];

  activeReference.value = props.referenceConfiguration ? props.referenceConfiguration.findIndex(r => r.reference === selectedReference.value) : -5;

  Object.entries(reference).map(([key, value]) => {
    const configuredValue = activeReference.value >= 0 ? props.referenceConfiguration[activeReference.value]?.values.find(configuredV => configuredV.value === key) || null : null;
    const isSystem = configuredValue ? configuredValue.system : true;
    buildetValue.push({
      key: key,
      label: configuredValue && configuredValue.label ? configuredValue.label : value,
      iconUrl: configuredValue && configuredValue.icon ? configuredValue.icon : null,
      systemReference: isSystem,
      disabled: configuredValue ? (!!configuredValue.system) : true,
      description: configuredValue ? configuredValue.description : ''
    });
  });

  return buildetValue.sort((a, b) => b.disabled - a.disabled);
});

function deleteReference (item) {
  const removeIndex = props.referenceConfiguration[activeReference.value].values.findIndex(configuredVal => configuredVal.value === item.key);
  // eslint-disable-next-line vue/no-mutating-props
  props.referenceConfiguration[activeReference.value].values.splice(removeIndex, 1);

  const references = JSON.parse(JSON.stringify(store.state.references));
  delete references[selectedReference.value][item.key];

  store.commit('setReferences', references);
}

function editItem (item) {
  valueLabel.value = item.label;
  valueKey.value = item.key;
  valueDescription.value = item.description;
  systemReference.value = item.systemReference;
  iconUrl.value = item.iconUrl;
  editMode.value = true;

  componentRerender.value++;
  dialog.value = true;
}

function closeDialog () {
  dialog.value = false;

  editMode.value = false;
  valueLabel.value = '';
  valueKey.value = '';
  valueDescription.value = '';
  iconUrl.value = '';
  referenceForm.value.reset();
}

function updateItems () {
  const object = {
    value: valueKey.value,
    label: valueLabel.value,
    system: systemReference.value,
    description: valueDescription.value
  };

  if (isLabalaguere.value) Object.assign(object, { icon: iconUrl.value });

  const references = JSON.parse(JSON.stringify(store.state.references));
  if (editMode.value && activeReference.value > -1) {
    const index = props.referenceConfiguration[activeReference.value].values.findIndex(v => v.value === valueKey.value);

    if (index >= 0) {
      // eslint-disable-next-line vue/no-mutating-props
      props.referenceConfiguration[activeReference.value].values[index] = object;
    } else {
      props.referenceConfiguration[activeReference.value].values.push(object);
    }
  } else {
    if (activeReference.value < 0) {
      const newReference = {
        reference: selectedReference.value,
        values: []
      };

      // eslint-disable-next-line vue/no-mutating-props
      props.referenceConfiguration.push(newReference);
      activeReference.value = props.referenceConfiguration.length - 1;
    }

    const reference = store.getters.getReference(selectedReference.value);

    // eslint-disable-next-line vue/no-mutating-props
    props.referenceConfiguration[activeReference.value].values.push({ ...object, system: !!reference[valueKey.value] });
  }

  references[selectedReference.value][valueKey.value] = valueLabel.value;
  store.commit('setReferences', references);

  closeDialog();
}
</script>
<style lang="css">
.table-color {
  background-color: #343434 !important;
}

.header-color {
  background-color: #5b5b5c !important;
}
</style>
